/**
 * MasterFormSection — shared Onboarding Form (Master-Form follow-up) task table.
 *
 * Used by:
 *   • Onboarding Form user Tasks tab  (mode="user")  — Start + Remarks
 *   • CX Leader Tasks (above CX Listings) (mode="manager") — Assign To dropdown
 *
 * Single fetch of /api/onboarding-form/tasks?status=total (+ active date range)
 * then bucketed client-side by status, so every tab count matches the Insights
 * cards (which use the same range anchored on created_at).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ClipboardList, Loader2, Play, Save, Pencil } from 'lucide-react';
import { rangeParams } from '../../components/DateRangeFilter';
import { toast } from 'sonner';
import { useTaskTable, TaskSearchBar, TablePagination } from '../tasks/TableControls';

const MF_SEARCH_KEYS = ['hotel_name'];

const authHeader = () => {
  const t = localStorage.getItem('token') || localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleString(); } catch { return iso; } };

const STATUS_STYLE = {
  unassigned: 'bg-gray-100 text-gray-600 border-gray-200',
  assigned: 'bg-blue-100 text-blue-700 border-blue-200',
  pending: 'bg-amber-100 text-amber-700 border-amber-200',
  completed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};
const StatusBadge = ({ s }) => (
  <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border capitalize ${STATUS_STYLE[s] || STATUS_STYLE.unassigned}`}>{s || 'unassigned'}</span>
);
const OverdueBadge = ({ label }) => {
  if (!label) return <span className="text-gray-400">—</span>;
  const cls = label === 'Critical' ? 'bg-red-100 text-red-700 border-red-200' : 'bg-orange-100 text-orange-700 border-orange-200';
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${cls}`}>{label}</span>;
};

const MANAGER_TABS = [
  { id: 'total', label: 'Total' },
  { id: 'assigned', label: 'Assigned' },
  { id: 'unassigned', label: 'Unassigned' },
  { id: 'completed', label: 'Completed' },
  { id: 'pending', label: 'Pending' },
];
const USER_TABS = [
  { id: 'assigned', label: 'Assigned' },
  { id: 'pending', label: 'Pending' },
  { id: 'completed', label: 'Completed' },
];

const MasterFormSection = ({ mode = 'user', range = 'all', customFrom = '', customTo = '', initialTab, testId = 'master-form-section' }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState('all');     // all | today
  const [tab, setTab] = useState(initialTab || (mode === 'manager' ? 'total' : 'assigned'));
  const [ofUsers, setOfUsers] = useState([]);
  const [busy, setBusy] = useState(null);
  const [editRemarks, setEditRemarks] = useState(null); // {id, value}

  // keep tab in sync when a parent deep-links via initialTab
  useEffect(() => { if (initialTab) setTab(initialTab); }, [initialTab]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ status: 'total', ...rangeParams(range, customFrom, customTo) }).toString();
      const res = await fetch(`${API}/api/onboarding-form/tasks?${qs}`, { headers: authHeader() });
      const data = await res.json();
      setRows(data.rows || []);
      if (mode === 'manager') {
        const ur = await fetch(`${API}/api/onboarding-form/users`, { headers: authHeader() });
        if (ur.ok) setOfUsers(await ur.json());
      }
    } catch (e) { toast.error('Failed to load Master Forms'); }
    finally { setLoading(false); }
  }, [range, customFrom, customTo, mode]);

  useEffect(() => { load(); }, [load]);

  const buckets = useMemo(() => ({
    total: rows,
    unassigned: rows.filter((r) => r.status === 'unassigned'),
    assigned: rows.filter((r) => r.status === 'assigned'),
    pending: rows.filter((r) => r.status === 'pending'),
    completed: rows.filter((r) => r.status === 'completed'),
    pending_union: rows.filter((r) => r.status === 'assigned' || r.status === 'pending'),
    overdue: rows.filter((r) => r.is_overdue),
  }), [rows]);

  const tabs = mode === 'manager' ? MANAGER_TABS : USER_TABS;
  const visible = (buckets[tab] || []).filter((r) => {
    if (scope === 'all') return true;
    const anchor = r.completed_at || r.assigned_at || r.created_at || '';
    return String(anchor).startsWith(new Date().toISOString().slice(0, 10));
  });

  // Independent property search + pagination (display only — tab counts above
  // stay derived from the full buckets).
  const { query, setQuery, page, setPage, totalPages, pageRows, total, pageSize } =
    useTaskTable(visible, { keys: MF_SEARCH_KEYS, pageSize: 10, resetKey: `${tab}:${scope}` });

  const assign = async (taskId, userId) => {
    if (!userId) return;
    setBusy(taskId);
    try {
      const res = await fetch(`${API}/api/onboarding-form/tasks/${taskId}/assign`, {
        method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ assignee_id: userId }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Assign failed');
      toast.success('Master Form assigned');
      await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const start = async (taskId) => {
    setBusy(taskId);
    try {
      const res = await fetch(`${API}/api/onboarding-form/tasks/${taskId}/start`, { method: 'POST', headers: authHeader() });
      if (!res.ok) throw new Error((await res.json()).detail || 'Start failed');
      toast.success('Started');
      await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const saveRemarks = async (taskId, value) => {
    setBusy(taskId);
    try {
      const res = await fetch(`${API}/api/onboarding-form/tasks/${taskId}/remarks`, {
        method: 'PUT', headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ remarks: value }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Save failed');
      setEditRemarks(null);
      await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const colCount = mode === 'manager' ? 9 : (mode === 'user' ? 9 : 8);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid={testId}>
      <div className="px-5 py-3.5 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2"><ClipboardList size={16} className="text-rose-600" /> Master Forms</h3>
        <div className="flex items-center gap-1 text-xs" data-testid={`${testId}-scope`}>
          {['today', 'all'].map((s) => (
            <button key={s} onClick={() => setScope(s)} className={`px-2.5 py-1 rounded-md capitalize ${scope === s ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600'}`} data-testid={`${testId}-scope-${s}`}>{s}</button>
          ))}
        </div>
      </div>
      <div className="px-5 py-3 border-b border-gray-100 flex flex-wrap gap-1.5">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`${testId}-tab-${t.id}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${tab === t.id ? 'bg-rose-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {t.label} <span className="opacity-70">({(buckets[t.id] || []).length})</span>
          </button>
        ))}
      </div>
      <div className="px-5 py-3 border-b border-gray-100">
        <TaskSearchBar query={query} setQuery={setQuery} testId={`${testId}-search`} />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={`${testId}-table`}>
          <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
            <tr>
              <th className="px-4 py-2.5 text-left">Hotel</th>
              <th className="px-4 py-2.5 text-left">Status</th>
              <th className="px-4 py-2.5 text-left">Assigned At</th>
              {mode === 'manager' && <th className="px-4 py-2.5 text-left">Assign To</th>}
              <th className="px-4 py-2.5 text-left">Deadline</th>
              <th className="px-4 py-2.5 text-left">Completed Time</th>
              <th className="px-4 py-2.5 text-left">Overdue</th>
              <th className="px-4 py-2.5 text-left">Overdue Time</th>
              <th className="px-4 py-2.5 text-left">Remarks</th>
              {mode === 'user' && <th className="px-4 py-2.5 text-left">Action</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={colCount} className="px-4 py-10 text-center text-gray-400"><Loader2 size={20} className="animate-spin inline" /></td></tr>
            ) : pageRows.length === 0 ? (
              <tr><td colSpan={colCount} className="px-4 py-10 text-center text-gray-400">No Master Forms in this view.</td></tr>
            ) : pageRows.map((r) => (
              <tr key={r.id} className="hover:bg-gray-50" data-testid={`${testId}-row-${r.id}`}>
                <td className="px-4 py-2.5 font-medium text-gray-800">{r.hotel_name || '—'}</td>
                <td className="px-4 py-2.5"><StatusBadge s={r.status} /></td>
                <td className="px-4 py-2.5 text-gray-600">{fmt(r.assigned_at)}</td>
                {mode === 'manager' && (
                  <td className="px-4 py-2.5">
                    {r.status === 'unassigned' ? (
                      <select value={r.assigned_to || ''} disabled={busy === r.id}
                        onChange={(e) => assign(r.id, e.target.value)} data-testid={`${testId}-assign-${r.id}`}
                        className="px-2 py-1 text-xs border border-gray-300 rounded-md bg-white max-w-[150px]">
                        <option value="">{r.assigned_to_name || 'Assign…'}</option>
                        {ofUsers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                      </select>
                    ) : (
                      <span className="text-gray-700 font-medium" data-testid={`${testId}-assigned-name-${r.id}`}>{r.assigned_to_name || '—'}</span>
                    )}
                  </td>
                )}
                <td className="px-4 py-2.5 text-gray-600">{r.deadline_label || '—'}</td>
                <td className="px-4 py-2.5 text-gray-600">{r.completed_time || '—'}</td>
                <td className="px-4 py-2.5"><OverdueBadge label={r.overdue} /></td>
                <td className="px-4 py-2.5 text-gray-600">{r.overdue_time || '—'}</td>
                <td className="px-4 py-2.5 text-gray-600 max-w-[200px]">
                  {editRemarks?.id === r.id ? (
                    <div className="flex items-center gap-1">
                      <input value={editRemarks.value} onChange={(e) => setEditRemarks({ id: r.id, value: e.target.value })}
                        className="px-2 py-1 text-xs border border-gray-300 rounded-md w-full" data-testid={`${testId}-remarks-input-${r.id}`} />
                      <button onClick={() => saveRemarks(r.id, editRemarks.value)} disabled={busy === r.id} className="text-emerald-600" data-testid={`${testId}-remarks-save-${r.id}`}><Save size={14} /></button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <span className="truncate">{r.remarks || '—'}</span>
                      <button onClick={() => setEditRemarks({ id: r.id, value: r.remarks || '' })} className="text-gray-400 hover:text-teal-600" data-testid={`${testId}-remarks-edit-${r.id}`}><Pencil size={12} /></button>
                    </div>
                  )}
                </td>
                {mode === 'user' && (
                  <td className="px-4 py-2.5">
                    {r.status === 'assigned' ? (
                      <button onClick={() => start(r.id)} disabled={busy === r.id}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50" data-testid={`${testId}-start-${r.id}`}>
                        {busy === r.id ? <Loader2 size={11} className="animate-spin" /> : <Play size={11} />} Start
                      </button>
                    ) : r.status === 'pending' ? (
                      <span className="text-xs text-amber-600">In progress</span>
                    ) : (
                      <span className="text-xs text-emerald-600">Done</span>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TablePagination page={page} totalPages={totalPages} setPage={setPage} total={total} pageSize={pageSize} testId={`${testId}-pagination`} />
    </div>
  );
};

export default MasterFormSection;
